'use client';
// Registers the offline-boot service worker inside the admin phone app, and in a non-production
// browser emulating it (BL-2341, BL-2450, ADR 066 — Offline Mode § 1). Mounted once in the root
// layout; renders nothing.
import { useEffect } from 'react';

import { getNativeDeviceGateway, wasNativeAppEmulationTurnedOff } from '../../gateways/native-device';
import { isOfflineBootEnabled, runOfflineBootRegistration, type ServiceWorkerContainerLike } from './offline-boot-registration';

const PREFIX = '[offline-boot]';

const log = {
    warn(message: string): void {
        // eslint-disable-next-line no-console -- client module; createLogger is server-only (ALS). Capacitor forwards it to Xcode / logcat.
        console.warn(`${PREFIX} ${message}`);
    },
    info(message: string): void {
        // eslint-disable-next-line no-console -- client module; createLogger is server-only (ALS). Capacitor forwards it to Xcode / logcat.
        console.info(`${PREFIX} ${message}`);
    },
    error(message: string, err: unknown): void {
        // eslint-disable-next-line no-console -- client module; createLogger is server-only (ALS). Capacitor forwards it to Xcode / logcat.
        console.error(`${PREFIX} ${message}`, err);
    },
};

interface WorkerLogMessage {
    offlineBoot: true;
    level: string;
    message: string;
}

function isWorkerLogMessage(data: unknown): data is WorkerLogMessage {
    return typeof data === 'object' && data !== null && (data as { offlineBoot?: unknown }).offlineBoot === true;
}

/**
 * Registers `/sw.js` when the page runs in the native app (or emulates it outside production)
 * and this build enables offline boot; removes it when the build doesn't, or when this page
 * turned the emulation off. Other desktop pages are left alone.
 * @returns nothing (renders no UI).
 * @sideEffect Registers or unregisters a service worker.
 */
export function ServiceWorkerRegistrar(): null {
    useEffect(() => {
        let cancelled = false;
        const container = 'serviceWorker' in navigator ? navigator.serviceWorker : undefined;
        const onMessage = (event: MessageEvent): void => {
            if (!isWorkerLogMessage(event.data)) return;
            const line = `sw ${event.data.level}: ${event.data.message}`;
            // The worker's own level survives, so a failed precache reads as an error in Xcode / logcat.
            if (event.data.level === 'error') log.error(line, new Error(event.data.message));
            else if (event.data.level === 'warn') log.warn(line);
            else log.info(line);
        };
        container?.addEventListener('message', onMessage);
        getNativeDeviceGateway()
            .then((gateway) => {
                if (cancelled) return undefined;
                return runOfflineBootRegistration({
                    isNativeApp: gateway.isNativeApp(),
                    emulationTurnedOff: wasNativeAppEmulationTurnedOff(),
                    enabled: isOfflineBootEnabled({
                        auctogonEnv: process.env.NEXT_PUBLIC_AUCTOGON_ENV,
                        nodeEnv: process.env.NODE_ENV,
                    }),
                    serviceWorker: container as ServiceWorkerContainerLike | undefined,
                    caches: typeof caches === 'undefined' ? undefined : caches,
                    isSecureContext: window.isSecureContext,
                    log,
                });
            })
            .catch((err: unknown) => log.error('registration failed', err));
        return () => {
            cancelled = true;
            container?.removeEventListener('message', onMessage);
        };
    }, []);
    return null;
}
